/**
 * 草图智能尺寸领域类型（阶段 2）。
 *
 * 设计说明：
 * 1. 本文件是尺寸聚合的纯数据协议：历史快照与 `.puppy` 持久化记录共用同一形态，
 *    避免同一份领域状态维护两套映射（与 `SketchPersistenceTypes.ts` 同范式）；
 * 2. 核心语义与显示语义分离（02 计划 §5.2）：`dimensionKind` 只表示求解量，
 *    径向/直径/线性等显示形态放在 `display`，`valueScale` 显式承载直径=2×半径、
 *    双倍距离=2×单侧距离的业务换算；
 * 3. 数值一律核心单位：长度米、角度弧度；UI 边界转换走 `utils/unit.ts`；
 * 4. 引用一律使用持久 ID（`cad:sketch-entity:*` / 点池 ID），禁止 Three.js 运行时 uuid；
 * 5. 3D 空间语义仅保留扩展位（2026-07-20 决策：3D 草图不在本轮范围）；
 * 6. 确定性要求：快照不携带时间戳/随机序号，数组顺序由业务字段（selectionOrder、
 *    dimensionId 字典序）决定。
 */

import type {OffsetChainRecord} from '../offset/type';

/** 可持久化的普通 JSON 数据（ghost 载荷等透传数据）。 */
export type SketchDimensionPlainData =
    | null
    | string
    | number
    | boolean
    | readonly SketchDimensionPlainData[]
    | {readonly [key: string]: SketchDimensionPlainData};

/** 尺寸仓库快照 schema 版本标识。 */
export const SKETCH_DIMENSION_SNAPSHOT_SCHEMA_VERSION_V1 = 'sketch-dimension-v1';

/**
 * 尺寸核心类型（求解语义，不含显示形态）。
 *
 * 说明：覆盖 02 计划 §5.1 核心类型集；阶段 2 只建立数据模型与校验，
 * 推断器（阶段 4）与求解方程（阶段 3）按此枚举逐项接入。
 */
export type SketchDimensionKind =
    | 'length'
    | 'point-distance'
    | 'point-line-distance'
    | 'line-line-distance'
    | 'angle'
    | 'three-point-angle'
    | 'radius'
    | 'diameter'
    | 'arc-length'
    | 'doubled-distance'
    | 'symmetric-angle'
    | 'spline-length'
    | 'curve-offset-distance'
    | 'path-length';

/**
 * 尺寸空间语义。
 *
 * 说明：本轮仅实现 `sketch-2d`；其余为 3D 扩展位，创建校验会拒绝。
 */
export type SketchDimensionSpace =
    | 'sketch-2d'
    | 'plane-3d-local'
    | 'global-3d-axis'
    | 'absolute-3d';

/** 线性尺寸方向分支。 */
export type SketchDimensionOrientation = 'aligned' | 'horizontal' | 'vertical';

/** 尺寸驱动角色。 */
export type SketchDimensionRole = 'driving' | 'driven';

/** 圆/圆弧引用附着条件（每个圆系引用独立保存）。 */
export type SketchDimensionArcCondition = 'center' | 'min' | 'max';

/** 引用拓扑角色。 */
export type SketchDimensionTopologyRole =
    | 'entity'
    | 'point'
    | 'endpoint-start'
    | 'endpoint-end'
    | 'center'
    | 'midpoint'
    | 'quadrant'
    | 'virtual-intersection'
    | 'spline-handle';

/**
 * 尺寸求解/引用状态。
 *
 * 语义对照 01 基线 §10.3：冲突（黄）与过定义（红）是不同状态；
 * dangling 不进入求解但保留身份与修复信息。
 */
export type SketchDimensionSolveState =
    | 'unknown'
    | 'under-defined'
    | 'fully-defined'
    | 'redundant'
    | 'conflicting'
    | 'unsolvable'
    | 'invalid'
    | 'dangling';

/** 参数量纲。 */
export type SketchDimensionQuantityKind = 'length' | 'angle';

/**
 * 尺寸稳定引用记录。
 *
 * 复合身份 = 实体持久 ID + 拓扑角色 + 可选点持久 ID/子实体路径 + 外部引用路径 + 选择顺序。
 */
export interface SketchDimensionReferenceRecord {
    /** 目标实体持久 ID；点引用时为宿主实体 ID，独立草图点为点自身 ID。 */
    readonly entityId: string;
    /** 拓扑角色。 */
    readonly topologyRole: SketchDimensionTopologyRole;
    /** 点持久 ID（指向草图点池）；拓扑角色为点类时必填。 */
    readonly pointId?: string;
    /** 子实体路径（槽口成员边等）。 */
    readonly subEntityPath?: string;
    /** 外部引用路径（模型边/顶点投影）；纯草图引用省略。 */
    readonly externalSourcePath?: string;
    /** 选择顺序（从 0 起）。 */
    readonly selectionOrder: number;
    /** 圆系附着条件；非圆系引用省略，圆系引用创建时默认 `center`。 */
    readonly arcCondition?: SketchDimensionArcCondition;
}

/**
 * 多解分支状态。
 *
 * 说明：保存并恢复求解分支（02 计划 §7.5）；阶段 2 只负责持久与历史往返，
 * 阶段 3 求解器按此延续原分支。全部字段可选，空对象表示无分支语义。
 */
export interface SketchDimensionBranchState {
    /** 线性方向分支。 */
    readonly orientation?: SketchDimensionOrientation;
    /** 两线角度象限（0-3）。 */
    readonly angleQuadrant?: 0 | 1 | 2 | 3;
    /** 方向符号。 */
    readonly directionSign?: 1 | -1;
    /** 中心线近/远侧。 */
    readonly centerlineSide?: 'near' | 'far';
    /**
     * 固定测量方向（单位向量，草图局部坐标）。
     *
     * 链尺寸语义（SW）：整条链的测量/标注方向继承最开始两点的连线方向，
     * 后续每对点的距离都是**沿该方向的投影**；aligned 布局与测量优先按此取向。
     */
    readonly fixedDirection?: readonly [number, number];
    /**
     * 链尺寸分组标识（同一次链命令产出的全部尺寸共享）。
     *
     * SW 语义：整条链的尺寸线共线（同在一条平行于链方向的轴上），
     * 拖动任意一条的标注，整组沿法向联动、保持共线。
     */
    readonly chainGroupId?: string;
    /**
     * 尺寸链（SW Ordinate 坐标尺寸）标记。
     *
     * 语义：references[0] 是共享基准点，值 = 目标点相对基准沿测量方向的
     * 坐标（投影绝对值）；布局为"引导线 + 各点界线 + 单向箭头"样式。
     */
    readonly ordinate?: boolean;
    /** 尺寸链组内第一条（负责附带绘制基准记号与 0 标注）。 */
    readonly ordinateBaseline?: boolean;
    /** 对称角模式：半角/完整角。 */
    readonly symmetricAngleMode?: 'half' | 'full';
    /** 3D 轴向扩展位（本轮不实现）。 */
    readonly axis?: 'x' | 'y' | 'z';
    /** 关联求解种子 ID（对应 solver island 的 SolverSolveSeed）。 */
    readonly solveSeedId?: string;
}

/** 显示单位覆盖标识（长度：mm/cm/m/in；角度：deg/rad）。 */
export type SketchDimensionUnitOverride = 'mm' | 'cm' | 'm' | 'in' | 'deg' | 'rad';

/**
 * 尺寸显示状态（不参与求解，渲染阶段消费；改任何显示字段不得改求解参数，DISP-001/002）。
 */
export interface SketchDimensionDisplayState {
    /** 标注位置（草图局部坐标，米）。 */
    readonly labelPosition?: readonly [number, number];
    /** 文字偏移（草图局部坐标，米）。 */
    readonly textOffset?: readonly [number, number];
    /** 显示样式：线性/径向/直径式。改样式不得改求解参数。 */
    readonly displayStyle?: 'linear' | 'radial' | 'diametric';
    /** 显示精度（小数位）。 */
    readonly precision?: number;
    /** 显示覆盖值；仅覆盖显示文本，不写回参数。null 表示未覆盖。 */
    readonly overrideValue?: string | null;
    /** 对称/上偏差（核心单位：米/弧度）；无公差省略。 */
    readonly tolerancePlus?: number;
    /** 下偏差（核心单位，正值语义为向下偏差幅值）；对称公差时与上偏差相等。 */
    readonly toleranceMinus?: number;
    /** 显示单位覆盖；省略时长度毫米、角度度。 */
    readonly unitOverride?: SketchDimensionUnitOverride;
    /** 是否隐藏尺寸界线。 */
    readonly extensionLinesHidden?: boolean;
    /**
     * 是否隐藏整条尺寸标注（尺寸线、箭头、标签全部不渲染）。
     *
     * 说明：
     * 1. 本字段只影响渲染与拾取，不改变尺寸在仓库、求解与持久化中的存在性——
     *    `hidden: true` 的尺寸仍作为 driving/driven 尺寸参与参数求解与 Undo/Redo；
     * 2. 供程序化创建接口（如 `createSketchDimensionInBatch`）在“只加约束、
     *    不显示智能尺寸 UI”的场景使用，用户交互创建的尺寸默认省略即可。
     */
    readonly hidden?: boolean;
}

/**
 * 尺寸参数绑定。
 */
export interface SketchDimensionParameterState {
    /** 参数稳定 ID（量纲类型化，由身份服务分配）。 */
    readonly parameterId: string;
    /** 名义值（业务值，核心单位：米/弧度）。 */
    readonly nominalValue: number;
    /** 参数量纲。 */
    readonly quantityKind: SketchDimensionQuantityKind;
    /**
     * 业务值 = 求解几何量 × valueScale。
     * 直径为 2（求解量是半径）、双倍距离为 2（求解量是单侧距离），其余为 1。
     */
    readonly valueScale: number;
    /** 表达式/全局变量绑定（阶段 7 实现）；无绑定为 null。 */
    readonly expression: string | null;
}

/**
 * 引用修复（悬空 ghost）状态。
 */
export interface SketchDimensionRepairState {
    /** 断链前的引用签名（由引用列表派生，用于 Reattach 合法性判断）。 */
    readonly originalReferenceSignature: string;
    /** ghost 显示数据（原实体同尺寸/形状/位置透传载荷）。 */
    readonly ghostData?: SketchDimensionPlainData;
}

/**
 * 尺寸聚合扩展元数据（可选的派生联动载荷）。
 *
 * 说明：仅承载与尺寸生命周期绑定的可序列化派生数据；当前只有等距实体自动尺寸
 * 写入等距链记录（改值时整链派生重算）。历史快照与 `.puppy` 持久化随尺寸主状态
 * 一起存取，因此撤销/重做/加载后联动关系自动恢复。
 */
export interface SketchDimensionMetadata {
    /** 等距链派生重算记录（等距实体自动尺寸专用）。 */
    readonly offsetChain?: OffsetChainRecord;
}

/**
 * 尺寸聚合完整快照（领域主状态）。
 *
 * 历史 adapter 快照与 `.puppy` 持久化记录直接复用本类型。
 */
export interface SketchDimensionSnapshot {
    /** 尺寸持久 ID（`cad:sketch-dimension:<uuid>`）。 */
    readonly dimensionId: string;
    /** 所属草图持久 ID。 */
    readonly sketchId: string;
    /** 尺寸名称（草图内唯一，如 D1）。 */
    readonly name: string;
    /** 核心类型。 */
    readonly dimensionKind: SketchDimensionKind;
    /** 空间语义。 */
    readonly space: SketchDimensionSpace;
    /** 驱动角色。 */
    readonly role: SketchDimensionRole;
    /** 是否被抑制。 */
    readonly suppressed: boolean;
    /** 是否只读（外部导入等场景）。 */
    readonly readOnly: boolean;
    /** 参数绑定。 */
    readonly parameter: SketchDimensionParameterState;
    /** driven 尺寸的测量值（核心单位）；尚未测量为 null。 */
    readonly measuredValue: number | null;
    /** 有序稳定引用（按 selectionOrder 升序）。 */
    readonly references: readonly SketchDimensionReferenceRecord[];
    /** 多解分支状态。 */
    readonly branch: SketchDimensionBranchState;
    /** 显示状态。 */
    readonly display: SketchDimensionDisplayState;
    /** 求解/引用状态。 */
    readonly solveState: SketchDimensionSolveState;
    /** 悬空修复状态；未悬空为 null。 */
    readonly repair: SketchDimensionRepairState | null;
    /** 扩展元数据（等距链联动等派生载荷）；无派生数据时省略。 */
    readonly metadata?: SketchDimensionMetadata;
}

/**
 * 单个草图的尺寸仓库快照（版本化信封）。
 *
 * `.puppy` 草图扩展与仓库整表恢复共用；`dimensions` 按 dimensionId 字典序。
 */
export interface SketchDimensionSketchSnapshot {
    /** schema 版本标识。 */
    readonly schemaVersion: typeof SKETCH_DIMENSION_SNAPSHOT_SCHEMA_VERSION_V1;
    /** 草图持久 ID。 */
    readonly sketchId: string;
    /** 尺寸快照列表（按 dimensionId 字典序）。 */
    readonly dimensions: readonly SketchDimensionSnapshot[];
}

/**
 * 创建尺寸的应用层输入（纯数据）。
 */
export interface CreateSketchDimensionInput {
    /** 所属草图持久 ID。 */
    readonly sketchId: string;
    /** 核心类型。 */
    readonly dimensionKind: SketchDimensionKind;
    /** 空间语义；省略时为 `sketch-2d`（本轮仅支持该值）。 */
    readonly space?: SketchDimensionSpace;
    /** 驱动角色。 */
    readonly role: SketchDimensionRole;
    /** 名义值（核心单位：米/弧度）；driven 尺寸传当前测量值。 */
    readonly nominalValue: number;
    /** driven 初始测量值；省略为 null。 */
    readonly measuredValue?: number | null;
    /** 有序稳定引用记录。 */
    readonly references: readonly SketchDimensionReferenceRecord[];
    /** 多解分支状态；省略为空对象。 */
    readonly branch?: SketchDimensionBranchState;
    /** 显示状态；省略为空对象。 */
    readonly display?: SketchDimensionDisplayState;
    /** 尺寸名称；省略时按 `D<n>` 自动分配。 */
    readonly name?: string;
    /** 业务值换算系数；省略时按核心类型推导（直径/双倍距离=2，其余=1）。 */
    readonly valueScale?: number;
    /** 扩展元数据（等距链联动等派生载荷）；无派生数据时省略。 */
    readonly metadata?: SketchDimensionMetadata;
}
